import { PButton, PInlineNotification } from "@porsche-design-system/components-react"
import { FormProvider } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import type { EditableEquipmentListProps } from "src/components"
import {
	ButtonGroup,
	FormLayout,
	FormLayoutGroup,
	EditableEquipmentList,
	InputField,
	MultiSelectField,
	SelectField,
} from "src/components"
import type {
	ChangeCustomWheelsFormProps,
	ChangeCustomWheelsProps,
	ChangePerformanceKitForm,
	ChangePerformanceKitProps,
	ChangeSelectedWheelsForm,
	ChangeSelectedWheelsProps,
} from "src/hooks"
import type { FormWithProps } from "src/types/FormWithProps"

export interface SpecialEquipmentEditProps {
	changeCustomWheels: FormWithProps<ChangeCustomWheelsFormProps, ChangeCustomWheelsProps> | undefined
	changePerformanceKit: FormWithProps<ChangePerformanceKitForm, ChangePerformanceKitProps>
	changeSelectedWheels: FormWithProps<ChangeSelectedWheelsForm, ChangeSelectedWheelsProps>
	editableEquipmentListProps: EditableEquipmentListProps
	hasSubmitFailed: boolean
	onCancel: () => void
	onResetToFactoryEquipment: () => void
	onSave: () => Promise<void>
	setHasSubmitFailed: (value: boolean) => void
	submitting: false | "save" | "reset" // TODO: change this to boolean and track "reset" or "save" mode separately
}

export const SpecialEquipmentEdit = ({
	changeCustomWheels,
	changePerformanceKit,
	changeSelectedWheels,
	editableEquipmentListProps,
	hasSubmitFailed,
	onCancel,
	onResetToFactoryEquipment,
	onSave,
	setHasSubmitFailed,
	submitting,
}: SpecialEquipmentEditProps) => {
	const { formatMessage } = useIntl()

	return (
		<FormLayoutGroup>
			<FormLayout $width="half">
				<FormProvider {...changeSelectedWheels.form}>
					<SelectField {...changeSelectedWheels?.props.option} />
				</FormProvider>
				{changeCustomWheels && (
					<FormProvider {...changeCustomWheels.form}>
						{changeCustomWheels.props.name.map((nameProps) => (
							<InputField key={nameProps.name} {...nameProps} />
						))}
						<MultiSelectField {...changeCustomWheels.props.tags} />
					</FormProvider>
				)}
				{changePerformanceKit.props.option.options.length > 0 && (
					<FormProvider {...changePerformanceKit.form}>
						<SelectField {...changePerformanceKit.props.option} />
					</FormProvider>
				)}
			</FormLayout>
			<EditableEquipmentList
				{...editableEquipmentListProps}
				onResetToFactoryEquipment={onResetToFactoryEquipment}
			/>
			{hasSubmitFailed && (
				<PInlineNotification
					state="error"
					description={formatMessage(formMessages.errorGeneral)}
					onDismiss={() => setHasSubmitFailed(false)}
				/>
			)}

			<ButtonGroup withoutMarginTop>
				<PButton loading={submitting === "save"} disabled={submitting === "reset"} onClick={onSave}>
					<FormattedMessage {...formMessages.save} />
				</PButton>
				<PButton type="button" variant="secondary" disabled={submitting !== false} onClick={onCancel}>
					<FormattedMessage {...formMessages.cancel} />
				</PButton>
			</ButtonGroup>
		</FormLayoutGroup>
	)
}
